import { Validator } from 'fluentvalidation-ts';
import { includes } from 'lodash';
import { numericInputStringToNumber } from '../../../../helpers/stringHelpers';
import { fieldRequiredValidationMessage } from '../../../../validators/sharedValidators';
import {
  beAnInteger,
  beEqualTo,
  beGreaterThanOrEqualTo,
  beLessThanOrEqualTo,
  beNumeric,
  bePositiveNumber,
  haveDecimalPlaces,
} from '../../../../validators/stringValidators';
import { RoomFloorConstructionFormModel } from '../GetDataForQuoteOrDesignForm';
import {
  allowedThermostatTypeCodesForElectricUnderfloorHeating,
  FloorLevelCode,
  ThermostatTypeCode,
  thermostatTypeCodesForElectricUnderfloorHeatingOnly,
  thermostatTypeCodesForOneSensorOnly,
} from '../quoteOrDesignFormData';
import { DesignRoomDetailsFormModel, DesignRoomDetailsFormModelValidator } from './designRoomDetailsFormModel';
import {
  RoomHeatLossDetailsFormModel,
  RoomHeatLossDetailsFormModelValidator,
} from './heatLoss/roomHeatLossDetailsFormModel';
import { RoomType } from './roomTypes';

export type RoomFormModel = {
  roomId: number | null;
  id: string;
  roomType: RoomType | null;
  name: string;
  roomTemperature: string;
  floorLevelCode: FloorLevelCode | null;
  floorConstruction: RoomFloorConstructionFormModel | null;
  areaInMetresSquared: string;
  numberOfThermostats: string;
  numberOfWetSensors: string;
  numberOfFloorSensors: string;
  numberOfPorts: string;
  perimeterInMetres: string;
  heatLossDetails: RoomHeatLossDetailsFormModel | null;
  designRoomDetails: DesignRoomDetailsFormModel | null;
} & (
  | {
      thermostatTypeCode: Exclude<ThermostatTypeCode, 'Shared'>;
      thermostatSharedWithId: null;
    }
  | {
      thermostatTypeCode: 'Shared';
      thermostatSharedWithId: string | null;
    }
);

export const defaultFloorLevelCode: FloorLevelCode | null = null;
export const defaultThermostatTypeCode: ThermostatTypeCode = 'NeoWhite';
export const defaultThermostatTypeCodeForRadiatorManifold: ThermostatTypeCode = 'UserSupplied';
export const initialRoomId = 'initial-room';

export const initialRoomFormModel: RoomFormModel = {
  roomId: null,
  id: initialRoomId,
  name: 'Room 1',
  floorLevelCode: defaultFloorLevelCode,
  floorConstruction: null,
  areaInMetresSquared: '',
  thermostatTypeCode: defaultThermostatTypeCode,
  thermostatSharedWithId: null,
  numberOfPorts: '',
  roomTemperature: '',
  roomType: null,
  numberOfThermostats: '1',
  numberOfWetSensors: '0',
  numberOfFloorSensors: '0',
  perimeterInMetres: '',
  heatLossDetails: null,
  designRoomDetails: null,
};

export class RoomFormModelValidator extends Validator<RoomFormModel> {
  constructor(belongsToRadiatorManifold: boolean, isDraft: boolean) {
    super();

    this.ruleFor('name')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .maxLength(100)
      .withMessage('Please enter a name that is no more than 100 characters long');

    this.ruleFor('floorLevelCode')
      .notNull()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('floorConstruction')
      .notNull()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => belongsToRadiatorManifold || isDraft);

    this.ruleFor('areaInMetresSquared')
      .notEmpty()
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1))
      .unless(() => isDraft);

    this.ruleFor('areaInMetresSquared')
      .must(beLessThanOrEqualTo(19))
      .when(room => !isDraft && room.floorConstruction != null && room.floorConstruction.isElectricUnderfloorHeating);

    this.ruleFor('numberOfPorts')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(bePositiveNumber)
      .must(beLessThanOrEqualTo(12))
      .must(beAnInteger)
      .unless(
        room =>
          belongsToRadiatorManifold ||
          isDraft ||
          (room.floorConstruction != null && room.floorConstruction.isElectricUnderfloorHeating),
      );

    this.ruleFor('roomTemperature')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(bePositiveNumber)
      .must(beAnInteger)
      .must(beLessThanOrEqualTo(100))
      .must((_, room) => {
        const roomTemperature = numericInputStringToNumber(room.roomTemperature);

        const maximumRoomTemperature =
          room.designRoomDetails == null
            ? null
            : numericInputStringToNumber(room.designRoomDetails.maximumTemperatureInDegreesCelsius);

        return roomTemperature == null || maximumRoomTemperature == null || roomTemperature <= maximumRoomTemperature;
      })
      .withMessage('Must be less than or equal to the Maximum Room Temperature')
      .unless(() => isDraft);

    this.ruleFor('thermostatTypeCode')
      .must(thermostatTypeCode => includes(allowedThermostatTypeCodesForElectricUnderfloorHeating, thermostatTypeCode))
      .withMessage('Cannot select this thermostat type when the floor construction is of type "Electric UFH"')
      .when(room => !isDraft && room.floorConstruction != null && room.floorConstruction.isElectricUnderfloorHeating);

    this.ruleFor('thermostatTypeCode')
      .must(thermostatTypeCode => !includes(thermostatTypeCodesForElectricUnderfloorHeatingOnly, thermostatTypeCode))
      .withMessage('Cannot select this thermostat type when the floor construction is not of type "Electric UFH"')
      .when(room => !isDraft && room.floorConstruction != null && !room.floorConstruction.isElectricUnderfloorHeating);

    this.ruleFor('thermostatTypeCode')
      .must(thermostatTypeCode => !includes(thermostatTypeCodesForOneSensorOnly, thermostatTypeCode))
      .withMessage('Cannot select this thermostat type when there are multiple sensors')
      .when(room => !isDraft && Number(room.numberOfWetSensors) + Number(room.numberOfFloorSensors) > 1);

    this.ruleFor('thermostatSharedWithId')
      .notNull()
      .withMessage('"Shared With" must have a value when thermostat type is "Shared"')
      .when(room => !isDraft && room.thermostatTypeCode === 'Shared');

    this.ruleFor('numberOfThermostats')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(beAnInteger)
      .must(beLessThanOrEqualTo(1))
      .unless(() => belongsToRadiatorManifold || isDraft);

    this.ruleFor('numberOfThermostats')
      .must(beEqualTo(0))
      .withMessage('Number of thermostats must be 0 when thermostat type is "Shared"')
      .when(room => !isDraft && room.thermostatTypeCode === 'Shared');

    this.ruleFor('numberOfWetSensors')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(beAnInteger)
      .must(beLessThanOrEqualTo(1000))
      .unless(() => belongsToRadiatorManifold || isDraft);

    this.ruleFor('numberOfWetSensors')
      .must(beEqualTo(0))
      .withMessage('Number of wet sensors must be 0 when thermostat type is "Shared"')
      .when(room => !isDraft && room.thermostatTypeCode === 'Shared');

    this.ruleFor('numberOfFloorSensors')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(beAnInteger)
      .must(beLessThanOrEqualTo(1000))
      .unless(() => belongsToRadiatorManifold || isDraft);

    this.ruleFor('numberOfFloorSensors')
      .must(beEqualTo(0))
      .withMessage('Number of floor sensors must be 0 when thermostat type is "Shared"')
      .when(room => !isDraft && room.thermostatTypeCode === 'Shared');

    this.ruleFor('perimeterInMetres')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(bePositiveNumber)
      .must(beAnInteger)
      .must(beLessThanOrEqualTo(1000000))
      .when(room => !isDraft && !!room.floorConstruction && room.floorConstruction.isSkirtingHeating);

    this.ruleFor('heatLossDetails').setValidator(
      room => new RoomHeatLossDetailsFormModelValidator(isDraft, room.floorConstruction),
    );

    this.ruleFor('designRoomDetails').setValidator(
      (room: RoomFormModel) => new DesignRoomDetailsFormModelValidator(room.floorConstruction, isDraft),
    );
  }
}
